import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import useVendorUser from "hooks/use-vendor-user";
import useAdminUser from "hooks/use-admin-user";
import useVendorClient from "hooks/use-vendor-client";
import useCustomerClient from "hooks/use-customer-client";
import useAdminClient from "hooks/use-admin-client";
import Button from "components/Button";
import { logout } from "lib/oauth";

const LogoutButton = styled(Button)`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  background-color: ${({ bgColor }) => bgColor || "transparent"};
  width: 100%;
  margin: 15px 0px;
  ${({ theme }) => theme.mq.sm`
  `}
`;

const IconWrapper = styled.div`
  margin-right: 12px;
  display: inline-block;

  svg {
    margin-bottom: 1px;
  }
`;

const LogoutActionItemWrapper = styled.div`
  height: 100%;

  cursor: default !important;
  padding: 0;
  opacity: 1;
  background-color: transparent;
  display: block;
  text-align: left;
  width: 100%;
  z-index: 10;
  border: none;
  line-height: 14px;

  a {
    padding: 0 !important;
  }

  a :hover {
    cursor: pointer;
    opacity: 0.5;
  }

  a :focus-visible {
    outline: 1px solid blue;
  }

  transition: ${({ theme }) => theme.opacityTransition};
`;

const LoginItemContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const TextWrapper = styled.div`
  color: ${({ color, theme }) => color || theme.themeColors.white};
  font-size: ${({ fontSize }) => fontSize || "14px"};
  margin-bottom: 2px;
  font-weight: 600;
  display: inline-block;
`;

const LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.themeColors.primary};
  border-radius: 48px;
  color: ${({ theme }) => theme.themeColors.white};
  width: 100%;
  height: 42px;
  font-size: 16px;

  ${({ theme }) => theme.mq.sm`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${theme.themeColors.primary};
    border-radius: 48px;
    color: ${theme.themeColors.white};
    height: 42px;
    font-size: 16px;
    margin: 0;
  `}
`;

const TransparentButton = styled.button`
  background-color: transparent;
  border: none;
  line-height: inherit;
  margin: 0;
  padding: 0;

  &:hover {
    cursor: pointer;
    opacity: 0.5;
  }

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 1px solid blue;
  }
`;

export default function LogoutButtonLink({
  Icon,
  redirectUrl,
  textContent,
  isButton,
  color,
  bgColor,
  fontSize,
}) {
  const vendorClient = useVendorClient();
  const customerClient = useCustomerClient();
  const adminClient = useAdminClient();

  const vendorUser = useVendorUser();
  const adminUser = useAdminUser();

  const logoutHandler = async () => {
    let ns = "customer";
    if (vendorUser) {
      ns = "provider";
    }
    if (adminUser) {
      ns = "admin";
    }
    await Promise.all([
      vendorClient.signOut(),
      customerClient.signOut(),
      adminClient.signOut(),
    ]);
    const next = new URL(
      // eslint-disable-next-line no-undef
      `${globalThis.location.origin}/${redirectUrl ?? `${ns}/auth`}`
    );
    await logout({ next });
  };

  return (
    <LoginItemContainer>
      {isButton ? (
        <LogoutActionItemWrapper>
          <LinkWrapper>
            <LogoutButton bgColor={bgColor} onClick={logoutHandler}>
              <TextWrapper fontSize={fontSize} color={color}>
                {textContent}
              </TextWrapper>
            </LogoutButton>
          </LinkWrapper>
        </LogoutActionItemWrapper>
      ) : (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <TransparentButton onClick={logoutHandler}>
          {Icon ? (
            <IconWrapper>
              <Icon color="#27272F" />
            </IconWrapper>
          ) : null}
          <TextWrapper fontSize={fontSize} color={color}>
            {textContent}
          </TextWrapper>
        </TransparentButton>
      )}
    </LoginItemContainer>
  );
}

LogoutButtonLink.propTypes = {
  Icon: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  redirectUrl: PropTypes.string,
  textContent: PropTypes.string,
  isButton: PropTypes.bool,
  color: PropTypes.string,
  bgColor: PropTypes.string,
  fontSize: PropTypes.string,
};

LogoutButtonLink.defaultProps = {
  Icon: null,
  textContent: "",
  isButton: false,
  color: "",
  bgColor: "",
  fontSize: "",
  redirectUrl: "/",
};
